import { Box } from '@chakra-ui/react'
import _ from 'lodash'
import type {
  FindItListQueryForPage,
  FindItListQueryForPageVariables,
} from 'types/graphql'

import { CellSuccessProps, MetaTags } from '@redwoodjs/web'

import ItListDetails from 'src/components/features/ItListDetails/ItListDetails'
import Footer from 'src/components/layout/Footer/Footer'
import CellFailure from 'src/components/widgets/CellFailure/CellFailure'
import Loader from 'src/components/widgets/Loader'
import MagicTopDrawer from 'src/components/widgets/MagicTopDrawer/MagicTopDrawer'
import MainLayout from 'src/layouts/MainLayout/MainLayout'
import { useResourcePageView } from 'src/lib/hooks/analytics/useResourcePageView'
import { useSetBrandLogo } from 'src/lib/hooks/useSetBrandLogo'
import { getFullName } from 'src/lib/utils'

import CreatorFooter from '../CreatorFooter/CreatorFooter'
import CreatorHeader from '../CreatorHeader/CreatorHeader'
import OtherItLists from '../OtherItLists/OtherItLists'

import useItListCell from './useItListCell'

export const QUERY = gql`
  query FindItListQueryForPage($id: Int!) {
    itList(id: $id) {
      id
      brandLogo
      createdAt
      updatedAt
      status
      coverUrl
      voiceIntroUrl
      hasVoiceIntro
      name
      description
      socialProofUrl
      price
      relationship
      affiliateId
      liked
      unlisted
      hasDraft
      disclaimer
      showDisclaimer
      purchaseDate
      unlockWithEmail
      draftId
      notes {
        order
        value
      }
      reviews {
        id
        coverUrl
      }
      channels {
        id
        isMain
        name
      }
      creator {
        id
        brandColorBrightness
        brandHue
        email
        firstName
        lastName
        profileImage
        username
        networkHandle
        itLists {
          id
          name
          updatedAt
          createdAt
          status
          order
          price
          relationship
          liked
          purchaseDate
          coverUrl
          channels {
            name
            id
            isMain
          }
        }
      }
      items {
        id
        type
        url
        title
        voiceTipUrl
        hasVoiceTip
        content {
          id
          coverUrl
          onlyAvailableOnItList
        }
        product {
          id
          coverUrl
          amazonUrl
          promoCodeCode
          promoCodeDiscount
          promoCodeValidFrom
          promoCodeValidTo
        }
        document {
          id
          type
          fileName
          coverUrl
          documentUrl
          onlyAvailableOnItList
        }
        text {
          id
          heading
          description
        }
        image {
          id
          coverUrl
          description
        }
      }
    }
  }
`

export const Loading = () => (
  <MainLayout>
    <Loader />
  </MainLayout>
)

export const Empty = () => (
  <MainLayout>
    <CellFailure
      title="The author may have deleted this ItList"
      subtitle="If you think this is a mistake please contact the author of this ItList directly"
    />
  </MainLayout>
)

export const Failure = () => (
  <MainLayout>
    <CellFailure />
  </MainLayout>
)

export const Success = ({
  itList,
}: CellSuccessProps<
  FindItListQueryForPage,
  FindItListQueryForPageVariables
>) => {
  useSetBrandLogo(itList.brandLogo)
  useResourcePageView({
    ownerEmail: itList.creator.email,
    ownerId: itList.creator.id,
    ownerUsername: itList.creator.username,
    hasPurchased: !!itList.purchaseDate,
  })

  const { locked, itLists, setLocked } = useItListCell(itList)
  const isOwner = itList.relationship === 'owner'

  return (
    <MainLayout
      withFooter={false}
      px={0}
      maxW="inherit"
      w="100%"
      itList={!locked && itList}
      headerButtonVariant="secondaryGrayOutline"
    >
      <MagicTopDrawer
        h="calc(100vh - 64px)"
        Drawer={
          isOwner &&
          (() => <CreatorHeader locked={locked} setLocked={setLocked} />)
        }
      >
        <MetaTags
          title={`${itList.name} by ${getFullName(itList.creator)} • @${
            itList.creator.username
          }`}
          description={itList.description}
        />
        <Box bg={{ base: 'transparent', lg: 'offWhite' }}>
          <Box
            px={{ base: 0, lg: 6 }}
            borderLeft={{ base: 0, lg: '1px solid #F2F2F2' }}
            borderRight={{ base: 0, lg: '1px solid #F2F2F2' }}
            bg="white"
            mx="auto"
            w={{ base: 'full', lg: '794px' }}
          >
            <ItListDetails
              itList={itList}
              locked={locked}
              items={itList.items}
            />

            {!!itLists.length && (
              <OtherItLists
                itLists={itLists}
                channels={itList.channels}
                creator={itList.creator}
              />
            )}
            {isOwner && <CreatorFooter itList={itList} />}
          </Box>
        </Box>
        <Footer
          pb={isOwner || locked ? 24 : 0}
          w={{ base: 'full', lg: 1200 }}
          mx="auto"
        />
      </MagicTopDrawer>
    </MainLayout>
  )
}

// cache-and-network fetch policy does not work here. It will set bad default values (from the cache)
// when you reopen an itlist (the same of EditItListCell)
export const beforeQuery = (props: FindItListQueryForPageVariables) => {
  return {
    variables: props,
    fetchPolicy: 'network-only',
  }
}
